import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Header from "./Header";
import { toWords } from "number-to-words";
import { useLocation, useNavigate } from "react-router-dom";

function UpdateUnreleased() {
  const { state } = useLocation();
  const [id, setId] = useState(() => state?.id || "");
  const [ott, setOtt] = useState([]);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ready, setReady] = useState([]);

  const [loading, setLoading] = useState(true);
  const [readyToRelease, setReadyToRelease] = useState({
    contentType: "",
    language: "",
    projectName: "",
    genre: "",
    contentDescription: "",
    trailer: "",
    trailerPassword: "",
    bannerName: "",
    productionHouse: "",
    producerOrInvestor: "",
    director: "",
    creativeDirector: "",
    writer: "",
    dop: "",
    socialMediaLinks: {},
    budget: 0,
    ottId: "",
  });

  const [socialMediaLinks, setSocialMediaLinks] = useState([
    { platform: "", link: "" },
  ]);
  const availablePlatforms = [
    "Facebook",
    "Instagram",
    "YouTube",
    "Twitter",
    "LinkedIn",
  ];

  const handleSocialMediaChange = (index, field, value) => {
    const updatedLinks = [...socialMediaLinks];
    updatedLinks[index][field] = value;
    setSocialMediaLinks(updatedLinks);
    setReadyToRelease((prev) => ({
      ...prev,
      socialMediaLinks: Object.fromEntries(
        updatedLinks
          .filter(({ platform, link }) => platform && link)
          .map(({ platform, link }) => [platform, link])
      ),
    }));
  };

  const handleAddLink = () => {
    setSocialMediaLinks([...socialMediaLinks, { platform: "", link: "" }]);
  };

  const handleRemoveLink = (index) => {
    const updatedLinks = socialMediaLinks.filter((_, i) => i !== index);
    setSocialMediaLinks(updatedLinks);
    setReadyToRelease((prev) => ({
      ...prev,
      socialMediaLinks: Object.fromEntries(
        updatedLinks
          .filter(({ platform, link }) => platform && link)
          .map(({ platform, link }) => [platform, link])
      ),
    }));
  };

  const getUnusedPlatforms = (currentPlatform) => {
    return availablePlatforms.filter(
      (platform) =>
        !socialMediaLinks.some(
          (link) =>
            link.platform === platform && link.platform !== currentPlatform
        )
    );
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setReadyToRelease((prevProject) => ({
      ...prevProject,
      [name]: name === "budget" ? parseFloat(value) || 0 : value,
    }));
  }, []);

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setReadyToRelease((prevProject) => ({
      ...prevProject,
      [name]: files.length > 1 ? [...files] : files[0],
    }));
  };

  // Fetch projects from the API when the component loads
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(
          `http://fundoramaa.co.in//api/v1/ready-to-release/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          }
        );
        const project = response.data?.data;
        if (project && typeof project === "object") {
          setReady([project]);
          setReadyToRelease((prev) => ({
            ...prev,
            ...project,
          }));
        } else {
          console.error("Unexpected response format:", response.data);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [id]);

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    // Append non-file fields
    formData.append("contentType", readyToRelease.contentType);
    formData.append("language", readyToRelease.language);
    formData.append("projectName", readyToRelease.projectName);
    formData.append("genre", readyToRelease.genre);
    formData.append("contentDescription", readyToRelease.contentDescription);
    formData.append("trailer", readyToRelease.trailer);
    formData.append("trailerPassword", readyToRelease.trailerPassword);
    formData.append("bannerName", readyToRelease.bannerName);
    formData.append("productionHouse", readyToRelease.productionHouse);
    formData.append("producerOrInvestor", readyToRelease.producerOrInvestor);
    formData.append("director", readyToRelease.director);
    formData.append("creativeDirector", readyToRelease.creativeDirector);
    formData.append("writer", readyToRelease.writer);
    formData.append("dop", readyToRelease.dop);
    formData.append(
      "socialMediaLinks",
      JSON.stringify(readyToRelease.socialMediaLinks)
    );
    formData.append("budget", readyToRelease.budget);
    formData.append("ottId", readyToRelease.ottId);

    // Append files
    if (readyToRelease.poster) formData.append("poster", readyToRelease.poster);
    if (readyToRelease.contract)
      formData.append("contract", readyToRelease.contract);
    if (readyToRelease.castCrewDetails)
      formData.append("castCrewDetails", readyToRelease.castCrewDetails);
    if (readyToRelease.nocFiles)
      formData.append("nocFiles", readyToRelease.nocFiles);
    if (readyToRelease.budgetSheet)
      formData.append("budgetSheet", readyToRelease.budgetSheet);

    try {
      const response = await axios.put(
        `http://fundoramaa.co.in//api/v1/ready-to-release/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.data) {
        setReady(response.data.data);
        setIsSubmitting(false);
        navigate(-1);
      } else {
        console.error("Unexpected response format:", response.data);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error updating project:", error);
      setIsSubmitting(false);
    }
  };

  // Fetch OTT from the API
  useEffect(() => {
    const fetchOtt = async () => {
      try {
        const response = await axios.get(
          "http://fundoramaa.co.in//api/v1/ott/"
        );
        if (response.data?.data && Array.isArray(response.data.data)) {
          setOtt(response.data.data);
        } else {
          console.error("Unexpected response format:", response.data);
        }
      } catch (error) {
        console.error("Error fetching otts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOtt();
  }, []);

  return (
    <>
      <div className="container">
        <div className="py-3">
          <div className="rounded-4">
            <div className="">
              <form onSubmit={handleUpdateProject}>
                <div className="py-2">
                  <div className="mb-3">
                    <span
                      onClick={() => window.history.back()}
                      class="back-button cursor-pointer"
                    >
                      <i className="bi bi-arrow-left h2"></i>
                    </span>
                    <div>
                      <h3 className="mb-0 lh-1">
                        <span className="text-reset">
                          {ready[0]?.projectName}
                        </span>
                      </h3>
                      <p className="m-0 text-capitalize">
                        {ready[0]?.bannerName}
                      </p>
                      <span
                        class={`badge px-4 mt-2 ${
                          ready[0]?.ottUpdates === "approved"
                            ? "bg-success-soft"
                            : "bg-danger-soft"
                        } mb-2 text-capitalize`}
                      >
                        {ready[0]?.ottUpdates}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="mb-3">
                      <p className="text-dark mb-1 bg-light py-2 text-center rounded-pill">
                        Content Type Selection
                      </p>
                    </div>
                    <div className="row g-3">
                      <div className="col-lg-6 col-12">
                        <label>
                          Select your type of content{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control mt-1"
                          name="contentType"
                          value={readyToRelease.contentType}
                          onChange={handleInputChange}
                          required
                          autoComplete="off"
                        >
                          <option value="">Select</option>
                          <option value="tvshow">TV Show</option>
                          <option value="webseries">Web Series</option>
                          <option value="movie">Movie</option>
                          <option value="shortfilm">Short Film</option>
                        </select>
                      </div>
                      <div className="col-lg-6 col-12">
                        <label>
                          Select language <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control mt-1"
                          name="language"
                          value={readyToRelease.language}
                          onChange={handleInputChange}
                          required
                          autoComplete="off"
                        >
                          <option value="">Select</option>
                          <option value="hindi">Hindi</option>
                          <option value="bengali">Bengali</option>
                          <option value="marathi">Marathi</option>
                          <option value="telugu">Telugu</option>
                          <option value="tamil">Tamil</option>
                          <option value="gujarati">Gujarati</option>
                          <option value="bhojpuri">Bhojpuri</option>
                          <option value="rajasthani">Rajasthani</option>
                          <option value="chhattisgarhi">Chhattisgarhi</option>
                          <option value="magahi">Magahi</option>
                          <option value="haryanvi">Haryanvi</option>
                          <option value="khortha/khotta">Khortha/Khotta</option>
                          <option value="marwari">Marwari</option>
                          <option value="bundeli/bundel khandi">
                            Bundeli/Bundel khandi
                          </option>
                          <option value="malvi">Malvi</option>
                          <option value="sadan/sadri">Sadan/Sadri</option>
                          <option value="mewari">Mewari</option>
                          <option value="awadhi">Awadhi</option>
                          <option value="lamani/lambadi">Lamani/Lambadi</option>
                          <option value="konkani">Konkani</option>
                          <option value="kurukh/oraon">Kurukh/Oraon</option>
                          <option value="tulu">Tulu</option>
                          <option value="manipuri">Manipuri</option>
                          <option value="sindhi">Sindhi</option>
                          <option value="pahari">Pahari</option>
                          <option value="hara/harauti">Hara/Harauti</option>
                          <option value="bagheli/baghel khandi">
                            Bagheli/Baghel Khandi
                          </option>
                          <option value="garhwali">Garhwali</option>
                          <option value="nimadi">Nimadi</option>
                          <option value="surjapuri">Surjapuri</option>
                          <option value="kumauni">Kumauni</option>
                          <option value="surgujia">Surgujia</option>
                          <option value="banjari">Banjari</option>
                          <option value="brajbhasha">Brajbhasha</option>
                          <option value="dhundhari">Dhundhari</option>
                          <option value="gojri/gujjari/gujar">
                            Gojri/Gujjari/Gujar
                          </option>
                          <option value="kangri">Kangri</option>
                          <option value="ahirani">Ahirani</option>
                          <option value="sambalpuri">Sambalpuri</option>
                          <option value="bagri">Bagri</option>
                          <option value="kachchhi">Kachchhi</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="mt-4 mb-3">
                      <p className="text-dark mb-1 bg-light py-2 text-center rounded-pill">
                        Project Details
                      </p>
                    </div>
                    <div className="row g-3">
                      <div className="col-lg-6 col-12">
                        <label>
                          Enter your project name{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control mt-1"
                          placeholder=""
                          name="projectName"
                          value={readyToRelease.projectName}
                          onChange={handleInputChange}
                          required
                          autoComplete="off"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label>
                          Select Genre <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control mt-1"
                          name="genre"
                          value={readyToRelease.genre}
                          onChange={handleInputChange}
                          required
                          autoComplete="off"
                        >
                          <option value="">Select</option>
                          <option value="Mystery Thriller">
                            Mystery Thriller
                          </option>
                          <option value="Historical Fiction">
                            Historical Fiction
                          </option>
                          <option value="Drama">Drama</option>
                          <option value="Mystery">Mystery</option>
                          <option value="Suspense">Suspense</option>
                          <option value="Action Adventures">
                            Action Adventures
                          </option>
                          <option value="Fantasy">Fantasy</option>
                          <option value="Romance">Romance</option>
                          <option value="Science Fiction">
                            Science Fiction
                          </option>
                          <option value="Relationship Fiction">
                            Relationship Fiction
                          </option>
                          <option value="Comedy">Comedy</option>
                          <option value="Love Story">Love Story</option>
                          <option value="Horror">Horror</option>
                          <option value="Crime">Crime</option>
                        </select>
                      </div>
                      <div className="col-lg-6 col-12">
                        <label>
                          Upload Poster{" "}
                          <span className="text-danger">(JPG, JPEG, PNG)</span>{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="file"
                          name="poster"
                          onChange={handleFileChange}
                          className="form-control mt-1"
                          autoComplete="off"
                          accept=".jpg, .jpeg, .png"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label>
                          Upload Contract{" "}
                          <span className="text-danger">(PDF, DOCX)</span>
                        </label>
                        <input
                          type="file"
                          name="contract"
                          onChange={handleFileChange}
                          className="form-control mt-1"
                          autoComplete="off"
                          accept=".jpg, .jpeg, .png, .pdf, .docx"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="pt-4">
                      <p className="text-dark bg-light py-2 text-center rounded-pill">
                        Form Fields for Content Submission
                      </p>
                    </div>
                    <div className="col-12">
                      <label>
                        Content Descriptions (One-line story / Synopsis){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <textarea
                        rows="5"
                        className="form-control mt-1"
                        name="contentDescription"
                        value={readyToRelease.contentDescription}
                        onChange={handleInputChange}
                        autoComplete="off"
                      ></textarea>
                    </div>
                    <div className="mt-2 col-lg-6 col-12">
                      <label>
                        Trailer / Teaser (URL){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="url"
                        className="form-control mt-1"
                        name="trailer"
                        value={readyToRelease.trailer}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                    </div>
                    <div className="mt-2 col-lg-6 col-12">
                      <label>
                        Trailer / Teaser (Password){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="password"
                        className="form-control mt-1"
                        name="trailerPassword"
                        value={readyToRelease.trailerPassword}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                    </div>
                    <div className="mt-2 col-lg-6 col-12">
                      <label>
                        Banner Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mt-1"
                        name="bannerName"
                        value={readyToRelease.bannerName}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        Production House Name{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mt-1"
                        placeholder=""
                        name="productionHouse"
                        value={readyToRelease.productionHouse}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        Producer / Investor{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mt-1"
                        placeholder=""
                        name="producerOrInvestor"
                        value={readyToRelease.producerOrInvestor}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        Director <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mt-1"
                        placeholder=""
                        name="director"
                        value={readyToRelease.director}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        Creative Director <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mt-1"
                        placeholder=""
                        name="creativeDirector"
                        value={readyToRelease.creativeDirector}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        Writer <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mt-1"
                        placeholder=""
                        name="writer"
                        value={readyToRelease.writer}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        Director of Photography (DOP){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mt-1"
                        placeholder=""
                        name="dop"
                        value={readyToRelease.dop}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        Cast & Crew Details{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="file"
                        className="form-control mt-1"
                        placeholder=""
                        name="castCrewDetails"
                        onChange={handleFileChange}
                        autoComplete="off"
                        accept=".xls, .pdf, .docx"
                      />
                    </div>
                    <div className="mt-2">
                      <label>Social Media Links</label>
                      {socialMediaLinks.map((link, index) => (
                        <div key={index} className="d-flex my-2">
                          <select
                            className="form-select me-2"
                            value={link.platform}
                            onChange={(e) =>
                              handleSocialMediaChange(
                                index,
                                "platform",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select Platform</option>
                            {getUnusedPlatforms(link.platform).map(
                              (platform) => (
                                <option key={platform} value={platform}>
                                  {platform}
                                </option>
                              )
                            )}
                          </select>
                          <input
                            type="url"
                            placeholder="Enter link"
                            className="form-control"
                            value={link.link}
                            onChange={(e) =>
                              handleSocialMediaChange(
                                index,
                                "link",
                                e.target.value
                              )
                            }
                          />
                          {index > 0 && (
                            <button
                              type="button"
                              className="btn btn-warning ms-2 rounded-circle"
                              onClick={() => handleRemoveLink(index)}
                            >
                              -
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm px-5 rounded-pill mt-2 mb-2"
                        onClick={handleAddLink}
                        disabled={
                          socialMediaLinks.length >= availablePlatforms.length
                        }
                      >
                        Add More
                      </button>
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="pt-3 pb-2">
                      <p className="text-dark mb-1 bg-light py-2 text-center rounded-pill">
                        Project Declaration
                      </p>
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        NOC file of all artist and all technicians{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="file"
                        className="form-control mt-1"
                        placeholder=""
                        name="nocFiles"
                        onChange={handleFileChange}
                        autoComplete="off"
                        accept=".jpg, .jpeg, .png, .pdf, .docx"
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        Selling Budget <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mt-1"
                        name="budget"
                        value={readyToRelease.budget}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                        maxLength={10}
                      />
                      {readyToRelease.budget > 0 && (
                        <div>
                          <p className="text-capitalize mb-0 text-dark mt-2">
                            ({toWords(readyToRelease.budget)})
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="col-lg-6 col-12 col-lg-6 col-12">
                      <label>
                        Budget Sheet (COP){" "}
                        <span className="text-danger">(PDF, Excel)</span>{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="file"
                        className="form-control mt-1"
                        name="budgetSheet"
                        onChange={handleFileChange}
                        autoComplete="off"
                        accept=".pdf, .xls"
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label>
                        Select OTT <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control mt-1"
                        name="ottId"
                        value={readyToRelease.ottId}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                      >
                        <option value="">Select</option>
                        {ott.map((ott) => (
                          <option value={ott._id}>{ott.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-6">
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg mt-5 mb-2 w-100 rounded-3 fs-5 text-white"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Updating ..." : "Update"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UpdateUnreleased;
